/* ==========================================================================
   Class Mathematics — Year 7 product page redesign
   Presentation only. All variant state is owned by Impulse (theme.Variants);
   this listens to the events it already fires and never drives it.
   Everything is defensive: if a hook is missing the page still works.
   ========================================================================== */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.cm7Init === 'true') return;
    root.dataset.cm7Init = 'true';

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-cm7-card]'));
    var panes = Array.prototype.slice.call(root.querySelectorAll('[data-cm7-pane]'));
    /* Price is echoed into its own span, never into [data-add-to-cart-text] —
       Impulse's updateCartButton() rewrites that node on every variant
       change and would overwrite anything we put there. */
    var priceEcho = root.querySelector('[data-cm7-price-echo]');

    /* ---- money ----------------------------------------------------------- */
    function money(cents) {
      try {
        if (window.theme && theme.Currency && theme.Currency.formatMoney) {
          return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
        }
      } catch (e) {}
      return '$' + (cents / 100).toFixed(2);
    }

    /* ---- selected card + callout ----------------------------------------- */
    /* Card highlight and callout pane follow the checked radio, so they stay
       correct even if the variant is changed from outside this component. */
    function paintSelection() {
      cards.forEach(function (card) {
        var input = card.querySelector('input[type="radio"]');
        if (input) card.classList.toggle('is-selected', !!input.checked);
      });

      var checked = root.querySelector('[data-cm7-card] input[type="radio"]:checked');
      var value = checked ? checked.value : null;
      if (value) {
        panes.forEach(function (pane) {
          pane.classList.toggle('is-active', pane.getAttribute('data-cm7-pane') === value);
        });
      }
    }

    function paint(variant) {
      paintSelection();

      // echo the selected price beside the add-to-cart label
      if (priceEcho) {
        if (variant && variant.available !== false && typeof variant.price === 'number') {
          priceEcho.textContent = '— ' + money(variant.price);
        } else {
          // sold out / unavailable: Impulse owns the label, show no price
          priceEcho.textContent = '';
        }
      }
    }

    /* Impulse dispatches 'variantChange' on the section container itself and
       'variant:change' on document. We listen on the container so events from
       another product section — a quick-shop modal, a recommendation — cannot
       repaint this one. */
    root.addEventListener('variantChange', function (evt) {
      if (evt.detail) paint(evt.detail.variant);
    });

    /* Repaint the highlight immediately on click, so the card responds even
       before Impulse resolves the variant. Price is left alone here — the
       variantChange handler owns it. */
    cards.forEach(function (card) {
      var input = card.querySelector('input[type="radio"]');
      if (input) input.addEventListener('change', paintSelection);
    });

    // initial state
    var initial = null;
    try {
      var json = root.querySelector('[data-cm7-initial-variant]');
      if (json) initial = JSON.parse(json.textContent);
    } catch (e) {}
    paint(initial);

    /* ---- gallery captions, badges and alt text --------------------------- */
    /* Captions are emitted per media by the section. Alt text is applied here
       as a stopgap — the durable fix is setting alt on each image in Shopify
       admin, which this cannot do. */
    var capData = {};
    try {
      var capEl = root.querySelector('[data-cm7-captions]');
      if (capEl) capData = JSON.parse(capEl.textContent) || {};
    } catch (e) {}

    /* The theme's fallback alt is the product title. Treat that as "unset"
       so captions replace it — real alt text entered in admin is respected. */
    var productTitle = root.getAttribute('data-product-title') || '';

    function decorate() {
      var slides = root.querySelectorAll('.product-main-slide');
      Array.prototype.forEach.call(slides, function (slide) {
        if (slide.dataset.cm7Done === 'true') return;
        var img = slide.querySelector('img');
        if (!img) return;

        // captions are keyed by slide index, stamped by the theme
        var meta = capData[slide.getAttribute('data-index')];
        if (!meta) return;
        slide.dataset.cm7Done = 'true';

        var alt = (img.getAttribute('alt') || '').trim();
        if (!alt || alt === productTitle) img.setAttribute('alt', meta.caption);

        var cap = document.createElement('span');
        cap.className = 'cm7-cap';
        cap.textContent = meta.caption;
        slide.appendChild(cap);

        if (meta.sample) {
          var badge = document.createElement('span');
          badge.className = 'cm7-badge';
          badge.textContent = 'SAMPLE';
          slide.appendChild(badge);
        }
      });
    }

    decorate();

    /* The slider clones slides (Flickity) and lazysizes swaps attributes, so
       watch both. childList alone misses the attribute swaps entirely. */
    if (window.MutationObserver) {
      var host = root.querySelector('[data-product-photos]');
      if (host) {
        new MutationObserver(function () { decorate(); }).observe(host, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['src', 'srcset', 'class', 'data-index']
        });
      }
    }
    // backstop for anything that lands outside the observer
    document.addEventListener('lazyloaded', decorate);

    /* ---- native tabs / accordion ----------------------------------------- */
    /* Only present once the product has its content in metafields; otherwise
       the tabs app owns this area and we touch nothing. */
    var tabWrap = root.querySelector('[data-cm7-tabs]');
    if (tabWrap) {
      var tabs = Array.prototype.slice.call(tabWrap.querySelectorAll('[data-cm7-tab]'));
      var tabPanels = Array.prototype.slice.call(tabWrap.querySelectorAll('[data-cm7-panel]'));

      function isDesktop() {
        return window.matchMedia('(min-width: 769px)').matches;
      }

      function setActive(key, allowClose) {
        var already = tabs.some(function (t) {
          return t.getAttribute('data-cm7-tab') === key && t.classList.contains('is-active');
        });
        // on mobile the accordion may close entirely; as tabs one stays open
        var close = allowClose && already && !isDesktop();

        tabs.forEach(function (t) {
          var on = !close && t.getAttribute('data-cm7-tab') === key;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-expanded', on ? 'true' : 'false');
        });
        tabPanels.forEach(function (p) {
          p.classList.toggle('is-active', !close && p.getAttribute('data-cm7-panel') === key);
        });
      }

      tabs.forEach(function (t) {
        t.addEventListener('click', function () {
          setActive(t.getAttribute('data-cm7-tab'), true);
        });
      });

      // never leave the desktop view with nothing open
      window.addEventListener('resize', function () {
        if (isDesktop() && !tabWrap.querySelector('.cm7-panel.is-active') && tabs.length) {
          setActive(tabs[0].getAttribute('data-cm7-tab'), false);
        }
      });
    }

    /* ---- "read the free chapter first" link ------------------------------ */
    /* The tab strip belongs to a third-party app, so this is best-effort:
       find a control whose text matches the fourth tab and click it. If the
       app changes its markup we simply scroll to it instead. */
    var leadLink = root.querySelector('[data-cm7-lead-link]');
    if (leadLink) {
      leadLink.addEventListener('click', function () {
        /* Prefer the free-chapter band — it is the real form, and once this
           product uses native tabs there is no "Download Free Chapter" tab
           to open. Fall back to the app's tab for products still on it. */
        var band = document.querySelector('[data-cm7-free-chapter]');
        if (band) {
          band.scrollIntoView({ behavior: 'smooth', block: 'start' });
          var firstField = band.querySelector('.cmp-first');
          if (firstField) {
            setTimeout(function () { try { firstField.focus({ preventScroll: true }); } catch (e) {} }, 600);
          }
          return;
        }

        var host = root.querySelector('[data-cm7-tabs-host]');
        if (!host) return;
        host.scrollIntoView({ behavior: 'smooth', block: 'start' });
        try {
          var wanted = 'download free chapter';
          var clickable = host.querySelectorAll('button, a, [role="tab"], summary, h5');
          for (var i = 0; i < clickable.length; i++) {
            var txt = (clickable[i].textContent || '').trim().toLowerCase();
            if (txt.indexOf(wanted) !== -1) { clickable[i].click(); break; }
          }
        } catch (e) {}
      });
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cm7-root]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor re-renders sections without a full page load
  document.addEventListener('shopify:section:load', boot);
})();
